/**
 * Open-redirect protection for `?redirect=…` query params (M58 / ADR-037 D9).
 *
 * Used by:
 *   - `RedeemBetaPage` — to validate redirect targets in the invite flow
 *   - `AuthPage` — to send users back to their pre-auth path after sign-in
 *
 * Allowlist: only paths matching either:
 *   - `/invite/<uuid>`
 *   - `/redeem?token=<uuid>`
 * are accepted. Anything else (path traversal, absolute URLs, partial UUIDs,
 * malformed UUIDs, etc.) falls back to `/`.
 *
 * UUIDs are matched against the canonical RFC 4122 hex shape used elsewhere
 * in the M58 invite flow (no version digit constraint — server SQL uses
 * `gen_random_uuid()` which is v4, but the validator accepts the shape so
 * tests can use synthetic tokens like `00000000-0000-0000-0000-000000000000`).
 *
 * Consensus-reviewer C10: extracted from `RedeemBetaPage.tsx` so it can be
 * shared with `AuthPage.tsx` without duplicating the regex.
 */

const INVITE_PATH_RE =
  /^\/invite\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const REDEEM_PATH_RE =
  /^\/redeem\?token=[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const FALLBACK_REDIRECT_PATH = '/';

/**
 * Validate a `?redirect=` value against the M58 allowlist.
 *
 * @param path  The candidate redirect path (string or null). May come from
 *              `searchParams.get('redirect')`, which is `string | null`.
 * @returns The original `path` if it matches the allowlist; otherwise `/`.
 */
export function validateRedirectPath(path: string | null): string {
  if (path == null) return FALLBACK_REDIRECT_PATH;
  if (INVITE_PATH_RE.test(path) || REDEEM_PATH_RE.test(path)) return path;
  return FALLBACK_REDIRECT_PATH;
}
